import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Bell, Slack, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PageTour } from "@/components/tour/PageTour";
import { notificationSettingsSteps } from "@/lib/tour-steps";

const eventCategories = [
  {
    category: "Clients",
    events: [
      { key: "new_client_created", label: "New client created" },
      { key: "client_churned", label: "Client churned/paused" },
    ],
  },
  {
    category: "Billing",
    events: [
      { key: "payment_succeeded", label: "Payment succeeded" },
      { key: "payment_failed", label: "Payment failed" },
      { key: "weekly_billing_summary", label: "Weekly billing summary" },
    ],
  },
  {
    category: "Demos",
    events: [
      { key: "demo_booked", label: "Demo booked" },
      { key: "demo_completed", label: "Demo completed" },
    ],
  },
];

interface Preference {
  event_type: string;
  slack_enabled: boolean;
  email_enabled: boolean;
}

export default function AdminNotificationSettings() {
  const [preferences, setPreferences] = useState<Record<string, { slack_enabled: boolean; email_enabled: boolean }>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  // Build default preferences for all event types
  const buildDefaults = () => {
    const defaults: Record<string, { slack_enabled: boolean; email_enabled: boolean }> = {};
    for (const cat of eventCategories) {
      for (const evt of cat.events) {
        defaults[evt.key] = { slack_enabled: true, email_enabled: true };
      }
    }
    return defaults;
  };

  useEffect(() => {
    const fetchPreferences = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      setUserId(user.id);

      const { data, error } = await supabase
        .from("notification_preferences")
        .select("event_type, slack_enabled, email_enabled")
        .eq("user_id", user.id);

      if (error) {
        console.error("Failed to fetch notification preferences:", error);
        toast.error("Failed to load notification preferences");
        setPreferences(buildDefaults());
      } else {
        const defaults = buildDefaults();
        // Override defaults with saved preferences
        if (data) {
          for (const pref of data as Preference[]) {
            defaults[pref.event_type] = {
              slack_enabled: pref.slack_enabled,
              email_enabled: pref.email_enabled,
            };
          }
        }
        setPreferences(defaults);
      }

      setLoading(false);
    };

    fetchPreferences();
  }, []);

  const togglePref = (eventKey: string, channel: "slack_enabled" | "email_enabled") => {
    setPreferences((prev) => ({
      ...prev,
      [eventKey]: {
        ...prev[eventKey],
        [channel]: !prev[eventKey]?.[channel],
      },
    }));
  };

  const handleSave = async () => {
    if (!userId) return;

    setSaving(true);
    try {
      const rows = Object.entries(preferences).map(([event_type, prefs]) => ({
        user_id: userId,
        event_type,
        slack_enabled: prefs.slack_enabled,
        email_enabled: prefs.email_enabled,
      }));

      const { error } = await supabase
        .from("notification_preferences")
        .upsert(rows, { onConflict: "user_id,event_type" });

      if (error) throw error;
      toast.success("Notification preferences saved");
    } catch (error: any) {
      toast.error(error.message || "Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <motion.div
        className="space-y-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div>
          <h1 className="text-2xl font-bold">Notification Settings</h1>
          <p className="text-muted-foreground mt-1">Configure how you receive notifications for key events</p>
        </div>
        <div className="max-w-2xl">
          <VentureCard>
            <VentureCardHeader>
              <VentureCardTitle>Preferences</VentureCardTitle>
            </VentureCardHeader>
            <VentureCardContent>
              <div className="animate-pulse space-y-4">
                <div className="h-8 bg-muted rounded" />
                <div className="h-8 bg-muted rounded" />
                <div className="h-8 bg-muted rounded" />
                <div className="h-8 bg-muted rounded" />
                <div className="h-8 bg-muted rounded" />
              </div>
            </VentureCardContent>
          </VentureCard>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <PageTour steps={notificationSettingsSteps} />
      <div>
        <h1 className="text-2xl font-bold">Notification Settings</h1>
        <p className="text-muted-foreground mt-1">Configure how you receive notifications for key events</p>
      </div>

      <div className="max-w-2xl">
        <VentureCard>
          <VentureCardHeader>
            <VentureCardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notification Preferences
            </VentureCardTitle>
          </VentureCardHeader>
          <VentureCardContent className="space-y-4">
            {/* Column headers */}
            <div className="flex items-center justify-end gap-8 text-xs font-medium text-muted-foreground pb-2 border-b border-border">
              <div className="flex items-center gap-1">
                <Slack className="w-3 h-3" />
                Slack
              </div>
              <div className="flex items-center gap-1">
                <Mail className="w-3 h-3" />
                Email
              </div>
            </div>

            {/* Event categories */}
            {eventCategories.map((cat) => (
              <div key={cat.category}>
                <p className="font-medium mb-2">{cat.category}</p>
                {cat.events.map((evt) => (
                  <div key={evt.key} className="flex items-center justify-between py-2">
                    <span className="text-sm text-muted-foreground">{evt.label}</span>
                    <div className="flex items-center gap-8">
                      <input
                        type="checkbox"
                        checked={preferences[evt.key]?.slack_enabled ?? true}
                        onChange={() => togglePref(evt.key, "slack_enabled")}
                        className="w-4 h-4 rounded border-border bg-surface accent-secondary"
                      />
                      <input
                        type="checkbox"
                        checked={preferences[evt.key]?.email_enabled ?? true}
                        onChange={() => togglePref(evt.key, "email_enabled")}
                        className="w-4 h-4 rounded border-border bg-surface accent-secondary"
                      />
                    </div>
                  </div>
                ))}
              </div>
            ))}

            <Button variant="gradient" className="w-full" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Preferences"}
            </Button>
          </VentureCardContent>
        </VentureCard>
      </div>
    </motion.div>
  );
}
